import TiptapImage from '@tiptap/extension-image';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Fragment, Slice } from '@tiptap/pm/model';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { InlineImageAttrs } from './inlineImageAttributes';
import {
  getImageAppearanceStyle,
  getImageSizeStyle,
  INLINE_IMAGE_NODE_NAME,
  numberAttribute,
  toStyleString,
} from './inlineImageAttributes';
import { InlineImageNodeView, RESIZE_HANDLES_CLASS } from './InlineImageNodeView';
import { insertInlineImageFromSrc } from './insertInlineImage';

export { INLINE_IMAGE_NODE_NAME };

export type InlineImageStorage = {
  // Set imperatively by the block/cell that owns this editor instance, which has
  // access to the document-scoped upload mutation (the extension itself does not).
  uploadImageFiles: ((files: File[]) => Promise<string[] | undefined>) | null;
  isInsertEnabled: boolean;
};

const getImageFiles = (dataTransfer: DataTransfer | null): File[] =>
  Array.from(dataTransfer?.files ?? []).filter((file) => file.type.startsWith('image/'));

const stripImageNodes = (fragment: Fragment): Fragment => {
  const nodes: ProseMirrorNode[] = [];
  fragment.forEach((node) => {
    if (node.type.name === INLINE_IMAGE_NODE_NAME) return;
    nodes.push(node.isLeaf ? node : node.copy(stripImageNodes(node.content)));
  });
  return Fragment.fromArray(nodes);
};

// Schema-only image node. It must be registered in every editor (edit, preview, table cells), whether or
// not inline images can be inserted: TipTap drops the whole document when it meets an unknown node type.
const InlineImageBase = TiptapImage.extend({
  name: INLINE_IMAGE_NODE_NAME,

  // A native <img> drag is picked up by react-dnd as an HTML drop, which would insert a copy
  // instead of moving the node.
  draggable: false,

  addAttributes() {
    return {
      ...this.parent?.(),
      width: numberAttribute('width', 'width', null),
      height: numberAttribute('height', 'height', null),
      lockAspectRatio: {
        default: true,
        parseHTML: (element) => element.getAttribute('data-lock-aspect-ratio') !== '0',
        renderHTML: (attributes) => ({ 'data-lock-aspect-ratio': attributes.lockAspectRatio ? '1' : '0' }),
      },
      opacity: numberAttribute('opacity', 'data-opacity', 100),
      borderWidth: numberAttribute('borderWidth', 'data-border-width', 0),
      borderColor: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-border-color') || null,
        renderHTML: (attributes) => (attributes.borderColor ? { 'data-border-color': attributes.borderColor } : {}),
      },
      borderRadius: numberAttribute('borderRadius', 'data-border-radius', 0),
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as InlineImageAttrs;
    const style = toStyleString({ ...getImageSizeStyle(attrs), ...getImageAppearanceStyle(attrs) });
    return ['img', { ...this.options.HTMLAttributes, ...HTMLAttributes, style }];
  },

  // The base extension turns typed ![alt](src) into an image, bypassing upload to the document library.
  addInputRules() {
    return [];
  },
}).configure({
  inline: true,
});

export const PreviewInlineImage = InlineImageBase;

export const EditorInlineImage = InlineImageBase.extend({
  addStorage(): InlineImageStorage {
    return {
      uploadImageFiles: null,
      isInsertEnabled: false,
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(InlineImageNodeView, {
      // On a selected node, ProseMirror makes the node draggable on mousedown, so dragging a resize handle
      // would start a native drag instead of resizing. Resize handle events are left to re-resizable.
      stopEvent: ({ event }) => event.target instanceof Element && !!event.target.closest(`.${RESIZE_HANDLES_CLASS}`),
    });
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const storage = this.storage as InlineImageStorage;

    return [
      new Plugin({
        key: new PluginKey('inline-image-insert'),
        props: {
          handlePaste: (view, event) => {
            const files = getImageFiles(event.clipboardData);
            if (!storage.isInsertEnabled || !storage.uploadImageFiles || files.length === 0) return false;

            event.preventDefault();
            const position = view.state.selection.from;
            storage.uploadImageFiles(files).then((srcs) => {
              srcs?.forEach((src) => insertInlineImageFromSrc(editor, src, position));
            });
            return true;
          },

          // Pasted HTML (e.g. copied from a web page) can carry <img> tags; drop them when inserting inline images is disabled.
          transformPasted: (slice) =>
            storage.isInsertEnabled ? slice : new Slice(stripImageNodes(slice.content), slice.openStart, slice.openEnd),
        },
      }),
    ];
  },
});
